import { Alert, Button, Stack, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { CitySelect } from "../../components/CitySelect";
import { EmailVerificationCard } from "../../components/EmailVerificationCard";
import { PhoneVerificationCard } from "../../components/PhoneVerificationCard";
import { ContentCard } from "../../components/ui";
import { useAuth } from "../../context/useAuth";
import { isIdentityVerified } from "../../lib/accountVerification";
import { affiliateApi } from "../../lib/api";
import { DeleteAccountCard } from "../../components/profile/DeleteAccountCard";
import { ApiError } from "../../lib/apiClient";

export function AffiliateProfilePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const profileQuery = useQuery({
    queryKey: ["affiliate-profile"],
    queryFn: () => affiliateApi.myProfile(),
  });

  const form = useForm({
    initialValues: {
      city: "",
    },
  });

  useEffect(() => {
    if (profileQuery.data) {
      form.setValues({
        city: profileQuery.data.city ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      affiliateApi.updateMyProfile({
        city: form.values.city.trim() || null,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Profile updated" });
      queryClient.invalidateQueries({ queryKey: ["affiliate-profile"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Update failed",
      });
    },
  });

  return (
    <Stack gap="lg" maw="var(--axio-unit-600)">
      <div>
        <Title order={2}>Profile</Title>
        <Text c="dimmed">Verify your contact details, then share your link with everyone.</Text>
      </div>

      {!isIdentityVerified(user) && (
        <Alert color="orange">
          Verify your email and phone below before creating or sharing your referral link.
        </Alert>
      )}

      <EmailVerificationCard />
      <PhoneVerificationCard />

      {profileQuery.data && (
        <Text size="sm">
          <Text span fw="var(--axio-font-weight-semibold)">
            {profileQuery.data.user.full_name}
          </Text>{" "}
          · {profileQuery.data.user.email}
        </Text>
      )}
      <ContentCard>
        <Stack gap="md">
          <CitySelect
            label="City"
            publicCatalog
            value={form.values.city}
            onChange={(city) => form.setFieldValue("city", city)}
          />
          <TextInput label="Status" value={profileQuery.data?.status ?? ""} readOnly />
          <Button color="teal" loading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            Save changes
          </Button>
        </Stack>
      </ContentCard>
      <DeleteAccountCard />
    </Stack>
  );
}
